import { Box, Grid } from "@dextech-ai/dex-design-system"
import Breadcrumbs from '@/components/Breadcrumbs'
import Button from '@/components/Button'
import Portal from '@/components/Portal'
import StepList from '@/components/StepList/index'
import Toast from '@/components/Toast'
import Tooltip from '@/components/Tooltip/Tooltip'
import useBlockchainNetworks from "@/hooks/use-blockchain-networks/index"
import usePage from '@/hooks/use-page'
import usePersona from '@/hooks/use-personas'
import useStepList from '@/hooks/use-step-list'
import useToast from '@/hooks/use-toast'
import useUseCases from '@/hooks/use-use-cases'
import { useNavigate, useParams } from '@/lib/router-dom'
import api from '@/services/api/orgPersonas.js'
import rem from '@/utils/rem'
import { getUseCaseId } from "@/utils/use-case-storage"
import { isEqual } from "lodash"
import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import DataElementCard from '../components/ConfirmationList/DataElementCard'
import ConfigureIssuanceSection from "./../etr-issuance/configure-issuance"
import { ConfigureIssuanceCard } from "./../etr-issuance/configure-issuance-card"
import Stepper from '@/componentsDS/Stepper/Stepper'

const Page = ({ role }) => {
	/*=================================================
		General
	/================================================*/

	const { organizationId, useCaseId } = useParams()
	const useCase = useMemo(() => getUseCaseId(useCaseId), [useCaseId]);

	const pageTitle = 'Edit Issuance Configuration'

	const { breadcrumbsItems } = usePage({ title: pageTitle, breadcrumbs: ['Use Cases', pageTitle] })

	const { goTo } = useNavigate()

	const user = useSelector(state => state.auth.user)

	const toastType = {
		success: "success",
		error: "error"
	}

	const [orgId, setOrgId] = useState("")

	const [etrIssuanceEnabled, setEtrIssuanceEnabled] = useState(false);
	const toggleEtrIssuance = () => setEtrIssuanceEnabled(!etrIssuanceEnabled);

	const [loading, setLoading] = useState(false)

	/*=================================================
		API
	/================================================*/

	const { toastList, addToastMessage } = useToast()

	const { find: findEnrolledPersonas, error: errorEnrolledPersona, items: personaItems, setItems: setPersonaItems } = usePersona()
	const { find: findUseCase, currentItem: useCaseItem, setCurrentItem: setUseCaseItem, error: errorUseCase } = useUseCases()
	const {
		blockchainNetworks,
		error: errorBlockchainNetworks,
		loading: loadingBlockchainNetworks,
		toggleBlockchainNetwork,
		toggleAllBlockchainNetworks,
		fetchBlockchainNetworks,
	} = useBlockchainNetworks(useCase?.etrIssuance?.networkIDs ?? useCaseItem?.[0]?.etrIssuance?.networkIDs ?? []);

	const isUseCaseEtrEnabled = useMemo(() => {
		return useCase?.enableETR ?? useCaseItem?.[0]?.enableETR
	}, [useCase, useCaseItem])
	const isOrgEtrEnabled = useMemo(() => {
		return useCase?.etrIssuance?.isEnabled ?? useCaseItem?.[0]?.etrIssuance?.isEnabled ?? false
	}, [useCase, useCaseItem])
	const existingNetworkIds = useMemo(() => useCase?.etrIssuance?.networkIDs ?? useCaseItem?.[0]?.etrIssuance?.networkIDs ?? [], [useCase, useCaseItem]);
	const selectedNetworkIds = useMemo(() => blockchainNetworks.filter(network => network.$selected).map(network => network.networkId), [blockchainNetworks]);
	const IsEtrPersonaArray = useMemo(() => personaItems.some(persona => persona.$selected && persona.is_etr_issuer), [personaItems]);

	const getUseCase = {
		useCaseId: useCaseId,
		organizationId: orgId
	}

	const enrollPersona = {
		useCaseId: useCaseId,
		orgId: orgId
	}
	useEffect(() => {
		if (role === 'admin') {
			setOrgId(organizationId)
		}
		if (role === 'participant') {
			setOrgId(user.organizationId)
		}
	}, [])

	useEffect(() => {
		if (isUseCaseEtrEnabled) {
			fetchBlockchainNetworks();
		}
	}, [isUseCaseEtrEnabled])

	useEffect(() => {
		async function getData() {
			const enrolledPersonasTemp = await findEnrolledPersonas(enrollPersona)

			setPersonaItems(enrolledPersonasTemp.map(persona => {
				persona.$selected = true;
				return persona;
			}))
		}

		async function getUseCaseData() {

			const selectedUseCaseItem = await findUseCase(getUseCase)
			setUseCaseItem(selectedUseCaseItem)

		}

		if (orgId !== "") {
			getData()
			getUseCaseData()
		}
	}, [orgId])

	useEffect(() => {
		setEtrIssuanceEnabled(useCase?.etrIssuance?.isEnabled ?? useCaseItem?.[0]?.etrIssuance?.isEnabled ?? false)
	}, [useCase, useCaseItem])

	useEffect(() => {
		addToastMessage(errorEnrolledPersona, toastType.error)
	}, [errorEnrolledPersona])

	useEffect(() => {
		addToastMessage(errorUseCase, toastType.error)
	}, [errorUseCase])

	/*=================================================
		Confirmation List
	/================================================*/

	const ConfirmationList = () => {
		return (
			<Grid
				columns={{ xs: 'minmax(0, 1fr)', md: '400px minmax(0, 1fr)' }}
				columnGap={0}
				rowGap={0}
				alignItems="start"
				sx={{ '& .layout-request__tile__content': { padding: 0 } }}
			>
				<div className="request-enrolment-admin__confirmation-tile request-enrolment-admin__confirmation-tile__left">
					<DataElementCard useCaseItem={useCaseItem} />
				</div>

				{isUseCaseEtrEnabled && (
					<ConfigureIssuanceCard
						blockchainNetworks={blockchainNetworks}
						noEtrPersonaSelected={!IsEtrPersonaArray}
						etrIssuanceEnabled={etrIssuanceEnabled}
						handleEdit={() => {
							goToStep(1)
						}}
					/>
				)}
			</Grid>
		)
	}

	/*=================================================
		Step
	/================================================*/

	const steps = useMemo(() => {
		return [
			{ stepTitle: 'Issuance Configuration' },
			{ stepTitle: 'Review & Confirm' },
		]
	}, [isUseCaseEtrEnabled])

	const { step, goToStep, prevStep, nextStep } = useStepList(steps)

	async function toUpdateETRIssuance() {
		if (loading) {
			return
		}

		setLoading(true)

		const payload = [{
			useCaseId: useCaseId,
			orgId: orgId,
			etrIssuance: {
				isEnabled: (etrIssuanceEnabled && IsEtrPersonaArray),
				networkIDs: selectedNetworkIds
			}
		}]

		const [error] = await api.enablePersona(payload)

		setLoading(false)

		if (error) {
			return addToastMessage(error?.message, toastType.error)
		}
	}

	function handleSuccessfulRedirect(messageType) {
		if (role === 'participant') {
			goTo(`/use-cases/${useCaseId}/personas/success`, { from: "editPersona", messageType: messageType })
		}
	}

	async function confirm() {
		if (hasETRIssuanceChanged()) {
			toUpdateETRIssuance();
			return handleSuccessfulRedirect('addPersonas');
		} else {
			addToastMessage("No changes detected", toastType.success)
		}
	}

	/*=================================================
		Navigate
	/================================================*/

	function onBack() {
		goTo(-1)
	}

	/*=================================================
		Calculated Values
	/================================================*/
	function hasETRIssuanceChanged() {
		return isOrgEtrEnabled !== (IsEtrPersonaArray && etrIssuanceEnabled) || !isEqual(existingNetworkIds.sort(), selectedNetworkIds.sort());
	}

	return (
		<>
			<div className="title-header edit-persona-header">
				<div className="title-header__left">
					<div className="title-header__title">
						{pageTitle}
					</div>
				</div>
				<Box display="flex" justify="end"></Box>
			</div>

			<Stepper steps={steps} currentStep={step-1}/>

			<div style={{ marginTop: rem(10) }}></div>

			{step === 1 && (
				<ConfigureIssuanceSection
					networkId={useCase?.etrIssuance?.networkIDs}
					setEtrIssuanceEnabled={setEtrIssuanceEnabled}
					status={useCase?.status}
					noEtrPersonaSelected={!IsEtrPersonaArray}
					etrIssuanceEnabled={etrIssuanceEnabled}
					toggleEtrIssuance={toggleEtrIssuance}
					blockchainNetworks={blockchainNetworks}
					loading={loadingBlockchainNetworks}
					error={errorBlockchainNetworks}
					toggleBlockchainNetwork={toggleBlockchainNetwork}
					toggleAllBlockchainNetworks={toggleAllBlockchainNetworks}
				/>
			)}

			{step === 2 && (
				<div className="layout-enroll">
					<div className="layout-enroll__content">
						<ConfirmationList />
					</div>
				</div>
			)}
			<div className='DS__layout__footer'>
				<div className="layout-default__sticky-actions">
					<div className="layout-default__sticky-actions__left">
						<Button
							data-testid="cancel-button"
							ghost
							noborder
							round
							text="Cancel"
							submit={() => onBack()}
						/>
					</div>
					<div className="layout-default__sticky-actions__right layout-default--flex">
						<div className="layout-default--margin-right">
							{step > 1 && (
								<Button
									data-testid="previous-button"
									round
									ghost
									text="Previous"
									submit={() => prevStep()}
								/>
							)}
						</div>
						{step === 1 && (
							<Button
								data-testid="next-button"
								round
								text="Next"
								submit={() => { nextStep() }}
							/>
						)}
						{step === 2 && (
							<Button
								id="edit-personas-submit-button"
								data-testid="save-button"
								round
								full
								disabled={loading}
								loading={loading}
								text="Save"
								submit={() => confirm()}
							/>
						)}
					</div>
				</div>
			</div>

			<Toast toastList={toastList} />
			<Tooltip />
		</>
	);
}

Page.propTypes = {
	role: PropTypes.string
}

export default Page
